import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Select from 'react-select'
import debounce from 'lodash/debounce'
import { get } from '../../api/axios'
import endpoints from '../../api/endpoints'
import {
  ChevronDropdown,
  customStyles,
} from './RoleSelect'

const DEBOUNCE_MS = 300
const PAGE_SIZE = 10
const MAX_FILTER_PAGES = 8

const isPastProject = (item = {}) => {
  const rawStatus = item?.status
  const status =
    typeof rawStatus === 'string' ? rawStatus : rawStatus?.status
  if (status === 'past' || status === 'completed') return true
  if (
    status === 'present' ||
    status === 'future' ||
    status === 'upcoming' ||
    status === 'in-progress'
  ) {
    return false
  }

  const end = item?.end_date ? new Date(item.end_date) : null
  if (!end || Number.isNaN(end.getTime()) || end >= new Date()) return false
  const start = item?.start_date ? new Date(item.start_date) : null
  if (start && !Number.isNaN(start.getTime()) && start > new Date()) {
    return false
  }
  return true
}

const mapToOptions = (list = []) =>
  list
    .filter(item => !isPastProject(item))
    .map(item => ({
      value: item.id,
      label: item.name || item.title || 'Untitled project',
      raw: item,
    }))
    .filter(item => item.value)

/**
 * ProjectSelect — RoleSelect-style searchable dropdown with paginated
 * infinite scroll against GET /api/v1/projects.
 * Past projects are excluded (create call informal / formal).
 */
const ProjectSelect = ({
  label,
  placeholder = 'Select a project',
  value = null,
  onChange,
  helperText,
  error,
  required = false,
  onBlur,
  menuPortalTarget = typeof document !== 'undefined' ? document.body : null,
}) => {
  const [inputValue, setInputValue] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [options, setOptions] = useState([])
  const [nextPageUrl, setNextPageUrl] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)

  const latestRequest = useRef(0)
  const abortRef = useRef(null)

  const selectedOption = useMemo(
    () => (value?.id ? { value: value.id, label: value.name } : null),
    [value]
  )

  const debouncedSetQuery = useMemo(
    () => debounce(q => setSearchQuery(q), DEBOUNCE_MS),
    []
  )

  const load = useCallback(
    async ({ query = '', pageUrl = null, isLoadMore = false } = {}) => {
      const requestId = ++latestRequest.current

      if (abortRef.current) {
        abortRef.current.abort()
      }
      const controller = new AbortController()
      abortRef.current = controller

      if (isLoadMore) {
        setIsLoading(false)
        setIsLoadingMore(true)
      } else {
        setIsLoadingMore(false)
        setIsLoading(true)
      }

      try {
        let mapped = []
        let next = pageUrl
        let pagesFetched = 0
        const minResults = isLoadMore ? 1 : PAGE_SIZE

        // Keep paging while past projects fill the page so the menu stays usable.
        do {
          const res = next
            ? await get(next, { signal: controller.signal })
            : await get(endpoints.getProjects, {
                signal: controller.signal,
                params: {
                  search: query,
                  page: 1,
                  page_size: PAGE_SIZE,
                  per_page: PAGE_SIZE,
                },
              })

          if (requestId !== latestRequest.current) return

          const result = res?.data?.result || []
          const list = Array.isArray(result) ? result : result?.data || []
          next = Array.isArray(result)
            ? res?.data?.next_page_url
            : result?.next_page_url
          mapped = [...mapped, ...mapToOptions(list)]
          pagesFetched += 1
        } while (
          next &&
          mapped.length < minResults &&
          pagesFetched < MAX_FILTER_PAGES &&
          requestId === latestRequest.current
        )

        if (requestId !== latestRequest.current) return

        if (isLoadMore) {
          setOptions(prev => {
            const seen = new Set(prev.map(opt => String(opt.value)))
            const unique = mapped.filter(
              opt => !seen.has(String(opt.value))
            )
            return [...prev, ...unique]
          })
        } else {
          setOptions(mapped)
        }
        setNextPageUrl(next || null)
      } catch {
        // Ignore aborted / failed loads; UI shows empty/loading states.
      } finally {
        if (requestId !== latestRequest.current) return
        if (isLoadMore) {
          setIsLoadingMore(false)
        } else {
          setIsLoading(false)
        }
      }
    },
    []
  )

  useEffect(() => {
    if (!menuOpen) return undefined
    load({ query: searchQuery })
    return undefined
  }, [menuOpen, searchQuery, load])

  useEffect(
    () => () => {
      if (abortRef.current) abortRef.current.abort()
      debouncedSetQuery.cancel()
    },
    [debouncedSetQuery]
  )

  const handleInputChange = useCallback(
    (newValue, { action }) => {
      if (action !== 'input-change') return
      setInputValue(newValue)
      debouncedSetQuery(newValue)
    },
    [debouncedSetQuery]
  )

  const handleChange = useCallback(
    option => {
      const id = option?.value || ''
      const name = option?.label || ''
      setInputValue('')
      setSearchQuery('')
      onChange?.(id ? { id, name } : null)
      setMenuOpen(false)
    },
    [onChange]
  )

  const handleMenuOpen = useCallback(() => {
    setMenuOpen(true)
    setSearchQuery(inputValue)
    debouncedSetQuery.cancel()
  }, [inputValue, debouncedSetQuery])

  const handleMenuClose = useCallback(() => {
    setMenuOpen(false)
    onBlur?.()
  }, [onBlur])

  const handleScrollToBottom = useCallback(() => {
    if (isLoading || isLoadingMore || !nextPageUrl) return
    load({ query: searchQuery, pageUrl: nextPageUrl, isLoadMore: true })
  }, [isLoading, isLoadingMore, nextPageUrl, load, searchQuery])

  return (
    <div className={`amok-field ${error ? 'amok-field--error' : ''}`}>
      {label && (
        <label className="amok-field__label">
          {label}
          {required && <span className="amok-field__required"> *</span>}
        </label>
      )}
      <Select
        className={`amok-role-select ${error ? 'amok-role-select--error' : ''}`}
        classNamePrefix="amok-role-select"
        inputValue={inputValue}
        onInputChange={handleInputChange}
        value={selectedOption}
        onChange={handleChange}
        options={options}
        isSearchable
        isClearable={false}
        openMenuOnFocus
        openMenuOnClick
        menuIsOpen={menuOpen}
        onMenuOpen={handleMenuOpen}
        onMenuClose={handleMenuClose}
        onMenuScrollToBottom={handleScrollToBottom}
        placeholder={placeholder}
        isLoading={isLoading || isLoadingMore}
        components={{
          DropdownIndicator: ChevronDropdown,
          IndicatorSeparator: null,
        }}
        noOptionsMessage={() => 'No projects found'}
        loadingMessage={() => 'Loading...'}
        menuPortalTarget={menuPortalTarget}
        menuPosition={menuPortalTarget ? 'fixed' : undefined}
        styles={{
          ...customStyles(!!error),
          ...(menuPortalTarget
            ? { menuPortal: base => ({ ...base, zIndex: 2000 }) }
            : {}),
        }}
      />
      {error && (
        <p className="amok-field__message amok-field__message--error">{error}</p>
      )}
      {!error && helperText && (
        <p className="amok-field__message">{helperText}</p>
      )}
    </div>
  )
}

export default ProjectSelect
